/**
 * Which brand this user last CHOSE, per workspace.
 *
 * Brand selection used to be in-memory only: `refreshBrand` re-derived it on
 * every mount, so a reload silently put the user back on the default brand.
 * With one brand that was invisible. With several it is a data-correctness bug
 * wearing a UX bug's clothes — you pick client B, reload, and every number on
 * screen is client A's under a heading you already stopped reading.
 *
 * ── THE RESOLUTION ORDER (the whole contract) ───────────────────────────────
 *
 *   1. URL param        `?brand=<id>`  — per TAB. Beats everything.
 *   2. stored preference               — per ACCOUNT+workspace, this browser.
 *   3. deterministic default           — first confirmed, else list[0].
 *   4. null                            — the workspace genuinely has no brand.
 *
 * (1) above (2) is what makes "compare two clients side by side" work. The URL
 * is the only per-tab storage a browser gives us for free: localStorage and
 * sessionStorage are per-origin and per-tab-session respectively, and NEITHER
 * lets two tabs of the same origin hold different values of the same key. So
 * two tabs, two `?brand=` params, two brands, no interference — and a reload of
 * either keeps its own. It is also linkable, which is the same property viewed
 * from the other side.
 *
 * ── WHY localStorage AND NOT sessionStorage ────────────────────────────────
 *
 * The brand drafts next door (lib/brandDraft.js) are sessionStorage precisely
 * so they die with the tab — an unconfirmed draft outliving the session is a
 * cross-tenant write waiting to happen. A preference is the opposite kind of
 * thing: it is not tenant DATA, it is a UI choice, it names an id the user
 * demonstrably has access to, and its whole job is to survive. Different
 * lifetime, different store, deliberately.
 *
 * ── NEVER PERSIST A PREFERENCE THE USER DID NOT CHOOSE ─────────────────────
 *
 * Only an explicit pick writes here — the brand switcher, and the confirm step
 * of "add brand" (choosing to create it is choosing to be on it). The
 * deterministic default at (3) must NOT write: if it did, the first brand the
 * app happened to derive would harden into a stored "choice" the user never
 * made, and the difference between "we defaulted you here" and "you asked for
 * this" would be gone the moment it mattered — namely when the default changes
 * and we have to decide whether to honour the stored value.
 *
 * Namespaced by workspace_id for the same reason the drafts are: a brand id is
 * only meaningful inside its workspace, and an un-namespaced key would hand
 * workspace B a preference naming a brand it cannot read. That resolves to a
 * 404 at best and, before `X-Workspace-Id` existed, to another tenant's data at
 * worst.
 */

const PREF_PREFIX = "lm_brand_pref:";

/** The query param that pins a brand to ONE tab. */
export const BRAND_PARAM = "brand";

/** True when we know which workspace we're in and may touch preference storage. */
export function hasBrandPrefScope(workspaceId) {
  return typeof workspaceId === "string" && workspaceId.length > 0;
}

function key(workspaceId) {
  return `${PREF_PREFIX}${workspaceId}`;
}

/** The brand id this user last explicitly chose in this workspace, or null. */
export function getBrandPref(workspaceId) {
  if (!hasBrandPrefScope(workspaceId)) return null;
  try {
    const v = localStorage.getItem(key(workspaceId));
    return v || null;
  } catch (_) {
    // Safari private mode / storage disabled. A missing preference is a
    // supported state (we fall through to the deterministic default), so this
    // degrades to "no memory" rather than to an error.
    return null;
  }
}

/**
 * Record an EXPLICIT choice. Callers: the brand switcher and the add-brand
 * confirm. Nothing else — see the note above.
 */
export function setBrandPref(workspaceId, brandId) {
  if (!hasBrandPrefScope(workspaceId) || !brandId) return;
  try {
    localStorage.setItem(key(workspaceId), brandId);
  } catch (_) {}
}

/**
 * Forget the preference for one workspace. Called when the preferred brand
 * turns out not to exist any more (deleted here or in another tab): a stored id
 * that 404s is worse than no stored id, because it re-fails on every load.
 */
export function clearBrandPref(workspaceId) {
  if (!hasBrandPrefScope(workspaceId)) return;
  try {
    localStorage.removeItem(key(workspaceId));
  } catch (_) {}
}

/**
 * Drop EVERY workspace's brand preference. Logout and user-change only —
 * mirrors `clearAllBrandDrafts`, and for the same reason: on a shared machine
 * none of these belong to whoever is now signed in.
 */
export function clearAllBrandPrefs() {
  try {
    const drop = [];
    for (let i = 0; i < localStorage.length; i++) {
      const k = localStorage.key(i);
      if (k && k.startsWith(PREF_PREFIX)) drop.push(k);
    }
    drop.forEach((k) => localStorage.removeItem(k));
  } catch (_) {}
}

/**
 * The `?brand=` of the CURRENT address bar, read straight from
 * `window.location` rather than from the router.
 *
 * That is deliberate and it is not a shortcut. AuthContext is mounted OUTSIDE
 * BrowserRouter (it has to be — it is what ProtectedRoute reads), so it cannot
 * call `useSearchParams`. The alternative was to let a component inside the
 * router apply the param AFTER the context had already resolved a brand, and
 * that alternative is broken in a way that matters: on a cold load of
 * `/app/find-customers?brand=B` the context would seat the default brand A,
 * every brand-scoped effect would fire for A, and only then would the sync
 * switch to B. There is a real frame — plus a round trip — in which A's numbers
 * are on screen under a URL that plainly says B. "Never render a tenant-derived
 * number without knowing it belongs to the current tenant" forbids exactly that.
 *
 * Reading the address bar is not routing. It is one string, it is available
 * synchronously before the first request, and it makes the URL a first-class
 * input to resolution instead of a correction applied to it.
 */
export function getBrandParam() {
  if (typeof window === "undefined") return null;
  try {
    return new URLSearchParams(window.location.search).get(BRAND_PARAM) || null;
  } catch (_) {
    return null;
  }
}

/**
 * The whole resolution order, in one place, given the authoritative list.
 *
 * Candidates are tried in order and each is honoured ONLY if it is actually in
 * the list. That containment check is the entire defence against a stale id:
 * a brand deleted in another tab leaves one behind in both the URL and the
 * stored preference, and seating it unchecked would start the exact 404 loop
 * B6 is about. An id that names nothing is simply not a candidate.
 *
 * The final two steps are the historical default — first confirmed, else
 * `list[0]` (the backend sorts oldest-first, so it is deterministic). Note that
 * `list[0]` can still promote an UNCONFIRMED draft to "the brand"; that is a
 * pre-existing defect (a draft brand_id is scannable) carried forward
 * unchanged, not something introduced here.
 */
export function resolveBrand(list, ...candidateIds) {
  const items = Array.isArray(list) ? list : [];
  for (const id of candidateIds) {
    if (!id) continue;
    const hit = items.find((b) => b.brand_id === id);
    if (hit) return hit;
  }
  return items.find((b) => b.status === "confirmed") || items[0] || null;
}
